import { useCallback, useEffect, useRef, useState } from 'react';
import globalAudio from '../lib/globalAudio.js';
import { formatAutoFlashInterval } from '../utils/time.js';

export function useUploadPlayback({
    setNotification
}) {
    const [fileName, setFileName] = useState('');
    const [mediaSrc, setMediaSrc] = useState(null);
    const [fileType, setFileType] = useState(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [currentTime, setCurrentTime] = useState(0);
    const [mediaDuration, setMediaDuration] = useState(0);
    const [isProcessing, setIsProcessing] = useState(false);
    const [uploadedFile, setUploadedFile] = useState(null);
    const [audioCacheId, setAudioCacheId] = useState(null);
    const [isUploadAutoFlashOn, setIsUploadAutoFlashOn] = useState(false);
    const [uploadAutoFlashInterval, setUploadAutoFlashInterval] = useState(20);

    const fileInputRef = useRef(null);
    const audioPlayerRef = useRef(null);
    const videoPlayerRef = useRef(null);
    const uploadAutoFlashTimerRef = useRef(null);
    const stableBlobRef = useRef(null); // Persist blob URL across re-renders

    // Infer media kind using MIME type with extension fallback (some browsers return empty type)
    const inferMediaKind = (file) => {
        const mime = (file?.type || '').toLowerCase();
        if (mime.startsWith('video/')) return 'video';
        if (mime.startsWith('audio/')) return 'audio';
        const name = (file?.name || '').toLowerCase();
        if (/\.(mp4|webm|mkv|mov|m4v)$/i.test(name)) return 'video';
        if (/\.(mp3|wav|m4a|aac|ogg|weba)$/i.test(name)) return 'audio';
        return null;
    };

    const triggerFileUpload = async () => {
        try {
            // Prefer modern picker to avoid focusing hidden inputs (prevents scroll jumps)
            if (window.showOpenFilePicker && typeof window.showOpenFilePicker === 'function') {
                const [handle] = await window.showOpenFilePicker({
                    multiple: false,
                    types: [
                        {
                            description: 'Audio',
                            accept: {
                                'audio/*': ['.mp3', '.wav', '.m4a', '.aac', '.ogg', '.weba'],
                                'audio/mp4': ['.m4a']
                            }
                        },
                        {
                            description: 'Video',
                            accept: {
                                'video/*': ['.mp4', '.webm', '.mkv', '.mov', '.m4v'],
                                'video/mp4': ['.mp4', '.m4v'],
                                'video/webm': ['.webm'],
                                'video/x-matroska': ['.mkv'],
                                'video/quicktime': ['.mov']
                            }
                        }
                    ]
                });
                const file = await handle.getFile();
                handleFileChange({ target: { files: [file] } });
                return;
            }
            // Fallback
            fileInputRef.current?.click();
        } catch (_) { fileInputRef.current?.click(); }
    };

    const handleFileChange = (event) => {
        const file = event.target?.files?.[0];
        console.log('[upload] handleFileChange fired', {
            hasEvent: !!event,
            hasTarget: !!event?.target,
            hasFiles: !!event?.target?.files,
            fileCount: event?.target?.files?.length || 0,
            name: file?.name,
            type: file?.type,
            size: file?.size
        });
        if (!file) return;
        
        // Clean up previous blob URL
        if (stableBlobRef.current) {
            try { URL.revokeObjectURL(stableBlobRef.current); } catch {}
            stableBlobRef.current = null;
        }
        
        setUploadedFile(file);
        setFileName(file.name);
        setCurrentTime(0);
        setMediaDuration(0);
        setAudioCacheId(null);

        const kind = inferMediaKind(file);
        if (!kind) {
            setNotification('Unsupported file type. Please upload an audio or video file.');
            console.warn('[upload] Unsupported file type', { type: file.type, name: file.name });
            return;
        }
        setFileType(kind);

        // Create a stable blob URL that won't change on re-renders
        try {
            const blobUrl = URL.createObjectURL(file);
            stableBlobRef.current = blobUrl;
            setMediaSrc(blobUrl);
            console.log('[upload] stable blob URL created:', blobUrl);
        } catch (e) {
            console.error('[upload] failed to create stable blob URL', e);
        }
        // Defer notification to prevent scroll jump from state update
        setTimeout(() => {
            setNotification('File selected. Press play and then flash it!');
            console.log('[upload] post-select snapshot', { fileName, fileType, hasMediaSrc: !!mediaSrc });
        }, 100);
    };

    useEffect(() => {
        let cleanup = null;
        let attachTimer = null;
        const attach = () => {
            const activePlayer = fileType === 'video' ? videoPlayerRef.current : (audioPlayerRef.current || globalAudio.get());
            if (!activePlayer) { attachTimer = setTimeout(attach, 50); return; }
            const updateDurationNow = () => {
                const d = activePlayer.duration;
                if (Number.isFinite(d) && d > 0) setMediaDuration(d);
            };
            const timeUpdate = () => {
                setCurrentTime(activePlayer.currentTime || 0);
                if (!Number.isFinite(mediaDuration) || mediaDuration === 0) updateDurationNow();
            };
            const loadedMeta = () => { updateDurationNow(); console.log('[upload] loadedmetadata', { duration: activePlayer.duration }); };
            const durationChange = () => { updateDurationNow(); console.log('[upload] durationchange', { duration: activePlayer.duration }); };
            const loadedData = () => { updateDurationNow(); };
            const canPlayThrough = () => { updateDurationNow(); };
            const onPlay = () => { console.log('[upload] media play'); };
            const onPlaying = () => { setIsPlaying(true); console.log('[upload] media playing'); };
            const onPause = () => { setIsPlaying(false); console.log('[upload] media pause'); };
            const onSeeked = () => { setCurrentTime(activePlayer.currentTime || 0); };

            activePlayer.addEventListener('timeupdate', timeUpdate);
            activePlayer.addEventListener('loadedmetadata', loadedMeta);
            activePlayer.addEventListener('durationchange', durationChange);
            activePlayer.addEventListener('loadeddata', loadedData);
            activePlayer.addEventListener('canplaythrough', canPlayThrough);
            activePlayer.addEventListener('play', onPlay);
            activePlayer.addEventListener('playing', onPlaying);
            activePlayer.addEventListener('pause', onPause);
            activePlayer.addEventListener('ended', onPause);
            activePlayer.addEventListener('seeked', onSeeked);

            cleanup = () => {
                try {
                    activePlayer.removeEventListener('timeupdate', timeUpdate);
                    activePlayer.removeEventListener('loadedmetadata', loadedMeta);
                    activePlayer.removeEventListener('durationchange', durationChange);
                    activePlayer.removeEventListener('loadeddata', loadedData);
                    activePlayer.removeEventListener('canplaythrough', canPlayThrough);
                    activePlayer.removeEventListener('play', onPlay);
                    activePlayer.removeEventListener('playing', onPlaying);
                    activePlayer.removeEventListener('pause', onPause);
                    activePlayer.removeEventListener('ended', onPause);
                    activePlayer.removeEventListener('seeked', onSeeked);
                } catch {}
            };
            // Initialize known duration immediately if available
            updateDurationNow();
        };
        attach();
        return () => { if (attachTimer) clearTimeout(attachTimer); if (cleanup) cleanup(); };
    }, [mediaSrc, fileType]);

    const togglePlayPause = useCallback(() => {
        const activePlayer = fileType === 'video' ? videoPlayerRef.current : (audioPlayerRef.current || globalAudio.get());
        console.log('[upload] togglePlayPause', { fileType, hasPlayer: !!activePlayer, paused: activePlayer?.paused, readyState: activePlayer?.readyState, duration: activePlayer?.duration });
        if (!activePlayer) return;
        if (activePlayer.paused) {
            const startPlayback = () => {
                const p = activePlayer.play();
                if (p && typeof p.then === 'function') {
                    p.then(() => setIsPlaying(true)).catch((err) => {
                        console.warn('[upload] play() blocked or failed', err);
                    });
                } else {
                    setIsPlaying(true);
                }
            };
            if (!Number.isFinite(activePlayer.duration) || activePlayer.duration === 0 || (activePlayer.readyState || 0) < 2) {
                const once = () => { activePlayer.removeEventListener('loadeddata', once); activePlayer.removeEventListener('canplay', once); startPlayback(); };
                activePlayer.addEventListener('loadeddata', once);
                activePlayer.addEventListener('canplay', once);
                try { activePlayer.load(); } catch (_) {}
            } else {
                startPlayback();
            }
        } else {
            activePlayer.pause();
            setIsPlaying(false);
        }
    }, [fileType]);

    const syncMediaState = useCallback(() => {
        const activePlayer = fileType === 'video' ? videoPlayerRef.current : (audioPlayerRef.current || globalAudio.get());
        if (!activePlayer) return;
        const d = activePlayer.duration;
        if (Number.isFinite(d) && d > 0) setMediaDuration(d);
        setCurrentTime(activePlayer.currentTime || 0);
        setIsPlaying(!activePlayer.paused);
        console.log('[upload] syncMediaState', { duration: activePlayer.duration, currentTime: activePlayer.currentTime, paused: activePlayer.paused });
    }, [fileType]);

    const handleSeek = useCallback((e) => {
        const activePlayer = fileType === 'video' ? videoPlayerRef.current : audioPlayerRef.current;
        const rect = e.currentTarget.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const width = rect.width || 1;
        const ratio = Math.max(0, Math.min(1, x / width));
        const seekTime = ratio * (Number.isFinite(mediaDuration) && mediaDuration > 0 ? mediaDuration : (activePlayer?.duration || 0));
        console.log('[upload] handleSeek', { seekTime, mediaDuration, duration: activePlayer?.duration });
        if (activePlayer) activePlayer.currentTime = seekTime;
    }, [fileType, mediaDuration]);

    const handleCancelUpload = () => {
        if (stableBlobRef.current) {
            try { URL.revokeObjectURL(stableBlobRef.current); } catch {}
            stableBlobRef.current = null;
        }
        setMediaSrc(null);
        setUploadedFile(null);
        setFileName('');
        setCurrentTime(0);
        setMediaDuration(0);
        setAudioCacheId(null);
        setFileType(null);
        setIsUploadAutoFlashOn(false);
        setNotification('');
        console.log('[upload] canceled');
    };

    const handleDrop = (event) => {
        event.preventDefault();
        const file = event.dataTransfer?.files?.[0];
        console.log('[upload] handleDrop', { fileCount: event.dataTransfer?.files?.length || 0, name: file?.name, type: file?.type, size: file?.size });
        if (!file) return;
        const syntheticEvent = { target: { files: [file] } };
        handleFileChange(syntheticEvent);
    };

    const handleDragOver = (event) => {
        event.preventDefault();
        // optional visual feedback can be added here
    };

    const rebuildPreview = () => {
        const file = uploadedFile;
        if (!file) return;
        console.log('[upload] rebuildPreview', { name: file.name, type: file.type });
        try {
            // Reuse the stable blob if it exists and matches the current file
            if (stableBlobRef.current && uploadedFile === file) {
                setMediaSrc(stableBlobRef.current);
                console.log('[upload] reusing existing stable blob URL');
                return;
            }
            
            // Clean up old blob if different file
            if (stableBlobRef.current) {
                try { URL.revokeObjectURL(stableBlobRef.current); } catch {}
            }
            
            const blobUrl = URL.createObjectURL(file);
            stableBlobRef.current = blobUrl;
            setMediaSrc(blobUrl);
            console.log('[upload] new stable blob URL created for rebuild');
        } catch (e) {
            console.error('[upload] failed to rebuild preview URL', e);
        }
    };

    useEffect(() => {
        console.log('[upload] state', { fileName, fileType, hasMediaSrc: !!mediaSrc, currentTime, mediaDuration });
    }, [fileName, fileType, mediaSrc, currentTime, mediaDuration]);

    // Cleanup blob URL on unmount
    useEffect(() => {
        return () => {
            if (stableBlobRef.current) {
                try { URL.revokeObjectURL(stableBlobRef.current); } catch {}
                stableBlobRef.current = null;
            }
        };
    }, []);

    return {
        // state
        fileName,
        mediaSrc,
        fileType,
        isPlaying,
        currentTime,
        mediaDuration,
        isProcessing,
        uploadedFile,
        audioCacheId,
        isUploadAutoFlashOn,
        uploadAutoFlashInterval,
        // setters
        setIsProcessing,
        setUploadedFile,
        setAudioCacheId,
        setIsUploadAutoFlashOn,
        setUploadAutoFlashInterval,
        // refs
        fileInputRef,
        audioPlayerRef,
        videoPlayerRef,
        uploadAutoFlashTimerRef,
        // controls
        triggerFileUpload,
        handleFileChange,
        handleDrop,
        handleDragOver,
        rebuildPreview,
        togglePlayPause,
        handleSeek,
        handleCancelUpload,
        syncMediaState
    };
}


